export interface Box {
  x: number;      // left coordinate
  y: number;      // top coordinate
  bottom: number; // bottom coordinate
  right: number;  // right coordinate
}

/**
 * Checks if a coordinate is vertically within a box (between top and bottom)
 */
function isVerticallyWithin(coordinate: { x: number; y: number }, box: Box): boolean {
  return coordinate.y >= box.y && coordinate.y <= box.bottom;
}

/**
 * Checks if a coordinate is fully within a box
 */
function isFullyWithin(coordinate: { x: number; y: number }, box: Box): boolean {
  return coordinate.x >= box.x &&
         coordinate.x <= box.right &&
         coordinate.y >= box.y &&
         coordinate.y <= box.bottom;
}

/**
 * Finds the box that is closest looking towards the top left of the coordinate.
 * Priority:
 * 1. Coordinate fully within an element
 * 2. Vertically within and to the left
 * 3. Latest box that's vertically above
 *
 * @param coordinate The x,y coordinate to search from
 * @param boxes Sequential array of boxes
 * @returns The closest box or null if none found
 */
export function prior<T extends Box>(
  coordinate: { x: number; y: number },
  boxes: T[]
): T | null {
  if (boxes.length === 0) return null;

  // Priority 1: Find boxes where coordinate is fully within
  for (let i = boxes.length - 1; i >= 0; i--) {
    const box = boxes[i];
    if (isFullyWithin(coordinate, box)) {
      return box;
    }
  }

  // Priority 2: Find boxes that are vertically within and to the left
  let bestVerticallyWithin: T | null = null;
  let bestVerticallyWithinDistance = Infinity;

  for (let i = boxes.length - 1; i >= 0; i--) {
    const box = boxes[i];
    if (isVerticallyWithin(coordinate, box) && box.right <= coordinate.x) {
      const distance = coordinate.x - box.right;
      if (distance < bestVerticallyWithinDistance) {
        bestVerticallyWithin = box;
        bestVerticallyWithinDistance = distance;
      }
    }
  }

  if (bestVerticallyWithin) {
    return bestVerticallyWithin;
  }

  // Priority 3: Find the latest box that's vertically above
  for (let i = boxes.length - 1; i >= 0; i--) {
    const box = boxes[i];
    if (box.bottom < coordinate.y) {
      return box;
    }
  }

  return null;
}

/**
 * Finds the box that is closest looking towards the bottom right of the coordinate.
 * Priority:
 * 1. Coordinate fully within an element
 * 2. Vertically within and to the right
 * 3. Earliest box that's vertically below
 *
 * @param coordinate The x,y coordinate to search from
 * @param boxes Sequential array of boxes
 * @returns The closest box or null if none found
 */
export function next<T extends Box>(
  coordinate: { x: number; y: number },
  boxes: T[]
): T | null {
  if (boxes.length === 0) return null;

  // Priority 1: Find boxes where coordinate is fully within
  for (const box of boxes) {
    if (isFullyWithin(coordinate, box)) {
      return box;
    }
  }

  // Priority 2: Find boxes that are vertically within and to the right
  let bestVerticallyWithin: T | null = null;
  let bestVerticallyWithinDistance = Infinity;

  for (const box of boxes) {
    if (isVerticallyWithin(coordinate, box) && box.x >= coordinate.x) {
      const distance = box.x - coordinate.x;
      if (distance < bestVerticallyWithinDistance) {
        bestVerticallyWithin = box;
        bestVerticallyWithinDistance = distance;
      }
    }
  }

  if (bestVerticallyWithin) {
    return bestVerticallyWithin;
  }

  // Priority 3: Find the earliest box that's vertically below
  for (const box of boxes) {
    if (box.y > coordinate.y) {
      return box;
    }
  }

  return null;
}
